// Script Execution Task Result Component
// Displays stdout, stderr, exit code for script execution tasks

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Layout,
  // Form,
  Text,
  // Icon,
  StatusTip,
} from '@tencent/tea-component';
import { Tag, Space, Table, Divider, Button, MessagePlugin } from 'tdesign-react';
import { ChevronDownIcon, ChevronUpIcon } from 'tdesign-icons-react';
import PropTypes from 'prop-types';
import { taskTimeFormat } from '@/utils/DateTime';
// import ButtonShareTask from '@/components/buttonShareTask';
import StartTaskComponent from '@/components/startTaskComponent';
import { TASK_TYPE_MAP, CODE_TO_TASK_STATUS_INFO, HOST_URL } from '@/config';
import axios from 'axios';
import './scriptResult.css';
import { Log } from '../../utils/Log';
import { TaskInfoCard } from '@/components/taskInfo';
import AICard from '@/components/aiCard';
const { Body, Content } = Layout;

// 资源类型配置映射
const RESOURCE_TYPE_CONFIG = {
  // 新的字符串格式
  cs: { name: '上下文切换', icon: '🔄', color: '#0052d9', category: 'CPU' },
  si: { name: '软中断', icon: '⚡', color: '#ed7b2f', category: 'CPU' },
  sys: { name: '系统态CPU', icon: '⚙️', color: '#0052d9', category: 'CPU' },
  usr: { name: '用户态CPU', icon: '👤', color: '#0052d9', category: 'CPU' },
  load: { name: '系统负载', icon: '📊', color: '#7356f1', category: 'CPU' },
  iowait: { name: 'CPU I/O等待', icon: '⏳', color: '#ed7b2f', category: 'CPU' },
  single_cpu: { name: 'CPU亲和性', icon: '🔧', color: '#e37318', category: 'CPU' },
  io: { name: '磁盘I/O情况', icon: '💿', color: '#ed7b2f', category: '硬盘' },
  mem: { name: '内存情况', icon: '💾', color: '#029cd4', category: '内存' },
  net: { name: '网络情况', icon: '🌐', color: '#7356f1', category: '网络' },
  dmesg: { name: 'OOM日志', icon: '📋', color: '#666666', category: '内存' },
};

const CATEGORY_DISPLAY_CONFIG = {
  CPU: { label: 'CPU', icon: '🧠', color: '#0052d9' },
  内存: { label: '内存', icon: '💾', color: '#029cd4' },
  硬盘: { label: '硬盘', icon: '💿', color: '#ed7b2f' },
  网络: { label: '网络', icon: '🌐', color: '#7356f1' },
};
const CATEGORY_ORDER = ['CPU', '内存', '硬盘', '网络'];
const DEFAULT_CATEGORY = 'CPU';

// 状态枚举
const STATUS = {
  NO_ANOMALY: { value: 0, label: '未发现异常', theme: 'success' },
  HAS_ANOMALY: { value: 1, label: '发现异常', theme: 'warning' }
};

const ScriptResult = (props) => {
  Log.debug('ScriptResult', props);
  const { taskInfo, script_output, goback, showGobackButton, agents } = props;
  // 展开状态管理
  const [expandedResources, setExpandedResources] = useState({});
  // 任务发起弹窗状态
  const [showStartTask, setShowStartTask] = useState(false);
  const [presetTaskParams, setPresetTaskParams] = useState(null);
  // 子任务列表状态
  const [subTasks, setSubTasks] = useState([]);
  const [subTasksTotal, setSubTasksTotal] = useState(0);
  const [subTasksPageIndex, setSubTasksPageIndex] = useState(1);
  const [subTasksPageSize, setSubTasksPageSize] = useState(5);
  // AI总结相关状态
  const [showAISummary, setShowAISummary] = useState(false);
  const [aiSummaryPrompt, setAiSummaryPrompt] = useState('');
  const [loadingAISummary, setLoadingAISummary] = useState(false);
  const [_aiSummaryAvailable, setAiSummaryAvailable] = useState(false); // AI总结是否可用
  const [aiSummaryGenerated, setAiSummaryGenerated] = useState(false); // AI总结是否已生成

  // 解析 taskResult
  const taskResult = script_output?.taskResult || {};
  const resourceResult = taskResult.resource_result || {};
  const comprehensiveAnalyse = taskResult.comprehensive_analyse || '';

  // 获取子任务列表
  const fetchSubTasks = () => {
    if (!taskInfo.tid) return;

    axios({
      method: 'get',
      url: `${HOST_URL}/api/v1/tasks/subtasks`,
      params: {
        master_tid: taskInfo.tid,
        page_num: subTasksPageIndex,
        page_size: subTasksPageSize,
      }
    })
      .then((response) => {
        Log.debug('获取子任务列表', response, response.data);
        // 直接检查 response.data 是否包含 tasks 数组
        if (response.data) {
          setSubTasks(response.data.tasks || []);
          setSubTasksTotal(response.data.total || 0);
        }
      })
      .catch((error) => {
        Log.error('获取子任务列表失败', error);
      })
      .finally(() => {
      });
  };

  // 检查AI总结是否可用
  const checkAISuggestionAvailable = useCallback(() => {
    if (!taskInfo.tid) return;

    axios({
      method: 'get',
      url: `${HOST_URL}/api/v1/task/suggestion/aisuggestion`,
      params: {
        task_id: taskInfo.tid,
      }
    })
      .then((response) => {
        Log.debug('检查AI总结可用性', response);
        // axios响应拦截器可能已经处理过，response可能直接就是data
        const resData = response;
        Log.debug('AI总结数据', resData, 'code:', resData?.code, 'data:', resData?.data);
        // 如果接口调用成功且返回了数据，直接展示AICard
        if (resData && resData.code === 0 && resData.data) {
          Log.debug('AI总结条件满足，设置状态');
          setAiSummaryAvailable(true);
          // 直接使用返回的数据作为prompt展示AICard
          setAiSummaryPrompt(resData.data);
          setShowAISummary(true);
          setAiSummaryGenerated(true); // 隐藏总结按钮
        }
      })
      .catch((error) => {
        Log.debug('AI总结暂不可用', error);
        setAiSummaryAvailable(false);
      });
  }, [taskInfo.tid]);

  // 初始加载和分页变化时获取子任务
  useEffect(() => {
    fetchSubTasks();
  }, [taskInfo.tid, subTasksPageIndex, subTasksPageSize]);

  // 页面加载时检查AI总结是否可用
  useEffect(() => {
    checkAISuggestionAvailable();
  }, [checkAISuggestionAvailable]);

  // 定时刷新子任务列表（每5秒）
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSubTasks();
    }, 5000);
    return () => clearInterval(interval);
  }, [taskInfo.tid, subTasksPageIndex, subTasksPageSize]);

  // Calculate execution duration
  // 切换展开/收起
  const toggleExpand = (resourceId) => {
    setExpandedResources(prev => ({
      ...prev,
      [resourceId]: !prev[resourceId]
    }));
  };

  // 获取状态配置
  const getStatusConfig = (statusValue) => {
    return statusValue === 1 ? STATUS.HAS_ANOMALY : STATUS.NO_ANOMALY;
  };

  // 渲染资源检测结果条
  const renderResourceBar = (resourceId, resourceData) => {
    // 兼容数字和字符串类型的resourceId
    const resourceConfig = RESOURCE_TYPE_CONFIG[resourceId]
      || RESOURCE_TYPE_CONFIG[Number(resourceId)] || {
        name: `资源 ${resourceId}`,
        icon: '📊',
        color: '#666',
        category: '其他'
      };
    const statusConfig = getStatusConfig(resourceData.status);
    const hasAnomaly = resourceData.status === 1;
    const isExpanded = expandedResources[resourceId];
    // 判断是否有内容可展示
    const hasContent = resourceData.content && resourceData.content.length > 0;
    // 判断是否可以展开：有info信息或content数据（无论是否有异常）
    const canExpand = resourceData.info || hasContent;
    // 判断是否支持下钻分析
    const canDrillDown = resourceData.profile === 1;

    return (
      <div key={resourceId} className={`task-bar ${canExpand ? 'expandable' : ''} ${isExpanded ? 'expanded' : ''}`}>
        <div
          className="task-bar-header"
          onClick={() => canExpand && toggleExpand(resourceId)}
          style={{ cursor: canExpand ? 'pointer' : 'default' }}
        >
          <div className="task-bar-left">
            <span className="resource-icon">{resourceConfig.icon}</span>
            <span className="task-name">{resourceConfig.name}</span>
          </div>

          <div className="task-bar-right">
            <Tag theme={statusConfig.theme} variant="light" size="small" className="task-status">
              {statusConfig.label}
            </Tag>
            {/* 未发现异常时，在状态旁边显示info摘要 */}
            {resourceData.info && (
              <span style={{ marginLeft: '8px', color: '#666', fontSize: '12px' }}>
                {resourceData.info.length > 30 ? `${resourceData.info.substring(0, 30)}...` : resourceData.info}
              </span>
            )}
            {canExpand && (
              <span className="expand-icon">
                {isExpanded ? <ChevronUpIcon size="20px" /> : <ChevronDownIcon size="20px" />}
              </span>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="task-bar-content">
            <div className="anomaly-detail">
              {/* 显示描述信息 */}
              {resourceData.info && (
                <div className="detail-section">
                  <div className="detail-label">{hasAnomaly ? '异常详情：' : '详情：'}</div>
                  <div className="detail-value">{resourceData.info}</div>
                </div>
              )}

              {/* 显示详细数据内容（如果有content数组） */}
              {hasContent && (
                <div className="detail-section" style={{ marginTop: resourceData.info ? '12px' : '0' }}>
                  <div className="detail-label">详细数据：</div>
                  <pre
                    style={{
                      background: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                      overflow: 'auto',
                      maxHeight: '300px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      margin: '8px 0 0 0',
                      whiteSpace: 'pre',
                      lineHeight: '1.5',
                    }}
                  >
                    {resourceData.content.join('\n')}
                  </pre>
                </div>
              )}

              {/* 下钻任务按钮（仅在有异常时显示） */}
              {hasAnomaly && canDrillDown && (
                <div className="detail-section" style={{ marginTop: '12px', textAlign: 'right' }}>
                  <Button
                    size="small"
                    theme="primary"
                    onClick={() => startDrillDownTask(resourceId)}
                  >
                    启动下钻任务
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // 渲染资源检测结果分组
  const renderResourceResults = () => {
    if (!resourceResult || Object.keys(resourceResult).length === 0) {
      return <div className="empty-state">暂无资源检测结果</div>;
    }

    const groupedResources = Object.entries(resourceResult).reduce((acc, [resourceId, resourceData]) => {
      const resourceConfig = RESOURCE_TYPE_CONFIG[resourceId]
        || RESOURCE_TYPE_CONFIG[Number(resourceId)] || { category: DEFAULT_CATEGORY };
      const normalizedCategory = CATEGORY_ORDER.includes(resourceConfig.category)
        ? resourceConfig.category
        : DEFAULT_CATEGORY;
      if (!acc[normalizedCategory]) {
        acc[normalizedCategory] = [];
      }
      acc[normalizedCategory].push({ resourceId, resourceData });
      return acc;
    }, {});

    return (
      <div className="tasks-container">
        {CATEGORY_ORDER.map((category) => {
          const resources = groupedResources[category];
          if (!resources || resources.length === 0) {
            return null;
          }
          const categoryConfig = CATEGORY_DISPLAY_CONFIG[category];
          return (
            <div key={category} className="resource-group">
              <div className="group-header" style={{ borderLeftColor: categoryConfig.color }}>
                <span className="group-icon">{categoryConfig.icon}</span>
                <span className="group-name">{categoryConfig.label}</span>
                <span className="group-count">({resources.length}项)</span>
              </div>
              <div className="group-tasks">
                {resources.map(({ resourceId, resourceData }) =>
                  renderResourceBar(resourceId, resourceData)
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 启动下钻任务
  const startDrillDownTask = (resourceId) => {
    const resourceConfig = RESOURCE_TYPE_CONFIG[resourceId]
      || RESOURCE_TYPE_CONFIG[Number(resourceId)] || {
        name: `资源 ${resourceId}`,
        icon: '📊',
        color: '#666'
      };

    Log.debug('启动下钻任务', resourceId);

    // 只保留下钻标记和父任务ID
    const params = {
      name: `${resourceConfig.name} - 深度分析`,
      target_host: [taskInfo.target_ip],
      drilldown_enable: true, // 标记为下钻任务
      master_tid: taskInfo.tid, // 关联父任务ID
    };

    setPresetTaskParams(params);
    setShowStartTask(true);
  };

  // 查看子任务详情
  const viewSubTask = (task) => {
    const url = `/task/result?taskID=${task.tid}&isSharedURL=false`;
    window.open(url, '_blank');
  };

  // 获取任务状态显示
  const getTaskStatus = (task) => {
    if (task.status === 4) {
      if (task.analysis_status === 0) {
        return <StatusTip status="loading" loadingText="分析中" />;
      }
      return <Button size="small" theme="primary" variant="text" onClick={() => viewSubTask(task)}>查看</Button>;
    }
    if (task.analysis_status === 3 || task.status === 3) {
      return <Text theme="danger">执行失败</Text>;
    }
    if (task.status !== 2) {
      return <StatusTip status="loading" loadingText="执行中" />;
    }
    if (task.analysis_status !== 2) {
      return <StatusTip status="loading" loadingText="分析中" />;
    }
    return <Button size="small" theme="primary" variant="text" onClick={() => viewSubTask(task)}>查看</Button>;
  };

  // 子任务表格列配置
  const subTasksColumns = [
    {
      colKey: 'name',
      title: '任务名称',
      width: 180,
      ellipsis: true,
    },
    {
      colKey: 'target_ip',
      title: '目标机器IP',
      width: 140,
      align: 'center',
    },
    {
      colKey: 'type',
      title: '任务类型',
      width: 120,
      align: 'center',
      cell: ({ row }) => TASK_TYPE_MAP[row.type] || '未知',
    },
    {
      colKey: 'create_time',
      title: '创建时间',
      width: 160,
      align: 'center',
      cell: ({ row }) => taskTimeFormat(row.create_time),
    },
    {
      colKey: 'status',
      title: '状态',
      width: 120,
      align: 'center',
      cell: ({ row }) => CODE_TO_TASK_STATUS_INFO(row.status * 10 + row.analysis_status),
    },
    {
      colKey: 'result',
      title: '操作',
      width: 100,
      align: 'center',
      cell: ({ row }) => getTaskStatus(row),
    },
  ];

  // 子任务分页变化处理
  const handleSubTasksPageChange = (pageInfo) => {
    setSubTasksPageIndex(pageInfo.current);
    setSubTasksPageSize(pageInfo.pageSize);
  };

  // 获取子任务的task_result
  const fetchSubTaskResult = useCallback((tid) => {
    return new Promise((resolve) => {
      axios({
        method: 'get',
        url: `${HOST_URL}/api/v1/task`,
        params: {
          tid: tid,
          is_shared_task: 0,
        }
      })
        .then((response) => {
          Log.debug('获取子任务结果', tid, response);
          if (response.code === 0 && response.data) {
            resolve(response.data);
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          Log.error('获取子任务结果失败', tid, error);
          resolve(null);
        });
    });
  }, []);

  // 触发AI任务总结
  const handleAISummary = async () => {
    if (loadingAISummary) return;

    setLoadingAISummary(true);
    try {
      // 收集当前任务的resource_result
      const summaryData = {
        master_task: {
          tid: taskInfo.tid,
          name: taskInfo.name,
          target_ip: taskInfo.target_ip,
          resource_result: resourceResult,
          comprehensive_analyse: comprehensiveAnalyse,
        },
        sub_tasks: [],
      };

      // 获取所有子任务的task_result
      if (subTasks && subTasks.length > 0) {
        const subTaskResults = await Promise.all(
          subTasks.map(async (subTask) => {
            const result = await fetchSubTaskResult(subTask.tid);
            let taskResult = null;
            if (subTask?.type == 10 || subTask?.type == 0 || subTask?.type == 1) {
              taskResult = result?.taskResult?.[""]?.top_funcs_callstacks;
            } else if (subTask?.type == 4 || subTask?.type == 7 || subTask?.type == 11) {
              taskResult = result?.taskResult;
            } else if (subTask?.type == 5) {
              const topFuncs = result?.taskResult?.[""]?.top_funcs || {};
              const goroutineTopFuncs = result?.taskResult?.[""]?.goroutine_top_funcs || {};
              // 将对象转为字符串进行拼接
              const topFuncsStr = JSON.stringify(topFuncs, null, 2);
              const goroutineTopFuncsStr = JSON.stringify(goroutineTopFuncs, null, 2);
              taskResult = `top_funcs:\n${topFuncsStr}\n\ngoroutine_top_funcs:\n${goroutineTopFuncsStr}`;
            } else if (subTask?.type == 6) {
              const resultList = result?.taskResult?.hprof_result;
              for (let i = 0; i < resultList.length; i++) {
                taskResult += resultList[i]?.message;
              }
            }
            return {
              tid: subTask.tid,
              name: subTask.name,
              type: subTask.type,
              target_ip: subTask.target_ip,
              task_result: taskResult,
            };
          })
        );
        summaryData.sub_tasks = subTaskResults.filter(item => item.task_result !== null);
        Log.debug('子任务结果', subTaskResults);
      }

      // 构建AI总结的prompt
      const prompt = `请对以下任务执行结果进行综合分析和总结：

      ## 主任务信息
      - 目标机器: ${summaryData.master_task.target_ip}
      - 检测结果: ${JSON.stringify(summaryData.master_task.resource_result, null, 2)}
      ${summaryData.master_task.comprehensive_analyse ? `- 检测指标分析: ${summaryData.master_task.comprehensive_analyse}` : ''}

      ## 子任务结果 (共${summaryData.sub_tasks.length}个)
      ${summaryData.sub_tasks.map((st, idx) => `
      ### 子任务${idx + 1}: ${st.name}
      - 类型: ${TASK_TYPE_MAP[st.type] || st.type}
      - 目标机器: ${st.target_ip}
      - 执行结果: ${JSON.stringify(st.task_result, null, 2)}
      `).join('')}
      `;

      setAiSummaryPrompt(prompt);
      setShowAISummary(true);
      setAiSummaryGenerated(true); // 标记AI总结已生成
      Log.debug('AI总结prompt', prompt);
    } catch (error) {
      Log.error('准备AI总结数据失败', error);
      MessagePlugin.error('准备AI总结数据失败，请重试');
    } finally {
      setLoadingAISummary(false);
    }
  };

  return (
    <Body>
      <Content>
        <Content.Header
          title="综合分析任务结果"
          showBackButton={showGobackButton}
          onBackButtonClick={goback}
        />
        <Content.Body full>
          <div className="script-result-container">
            {/* Task Info Card */}
                <TaskInfoCard
                  task_info={taskInfo}>
                </TaskInfoCard>
            {/* Resource Detection Results */}
            {resourceResult && Object.keys(resourceResult).length > 0 && (
              <Card style={{ marginTop: '20px' }}>
                <Card.Body title="检测结果">
                  {renderResourceResults()}
                </Card.Body>
              </Card>
            )}

            {/* Comprehensive Analysis */}
            {comprehensiveAnalyse && comprehensiveAnalyse.length > 0 && (
              <Card style={{ marginTop: '20px' }}>
                <Card.Body title="检测指标分析">
                  <div
                    style={{
                      background: '#f9f9f9',
                      padding: '16px',
                      borderRadius: '4px',
                      lineHeight: '1.8',
                      fontSize: '14px',
                      color: '#333',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {comprehensiveAnalyse}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Triggered Drilldown Tasks */}
            <Card style={{ marginTop: '20px' }}>
              <Card.Body title={`已触发的下钻任务 (${subTasksTotal})`}>
                {subTasks.length > 0 ? (
                  <Table
                    data={subTasks}
                    columns={subTasksColumns}
                    rowKey="id"
                    size="small"
                    stripe
                    bordered
                    pagination={{
                      current: subTasksPageIndex,
                      pageSize: subTasksPageSize,
                      total: subTasksTotal,
                      showJumper: true,
                      onChange: handleSubTasksPageChange,
                    }}
                  />
                ) : (
                  <div className="empty-state">暂无已触发的下钻任务</div>
                )}
              </Card.Body>
            </Card>

            <Divider />

            {/* Script Content Card */}
            {script_output.script_content && (
              <Card style={{ marginTop: '20px' }}>
                <Card.Body title="脚本内容">
                  <pre
                    style={{
                      background: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                      overflow: 'auto',
                      maxHeight: '300px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                    }}
                  >
                    {script_output.script_content}
                  </pre>
                </Card.Body>
              </Card>
            )}

            {/* Standard Output Card */}
            {script_output.stdout_output !== undefined && script_output.stdout_output !== null && (
              <Card style={{ marginTop: '20px' }}>
                <Card.Body
                  title={
                    <Space>
                      <span>标准输出 (stdout)</span>
                      <Tag theme="primary">
                        {script_output.stdout_output.length} 字节
                      </Tag>
                    </Space>
                  }
                >
                  {script_output.stdout_output ? (
                    <pre
                      style={{
                        background: '#f5f5f5',
                        padding: '12px',
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '400px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {script_output.stdout_output}
                    </pre>
                  ) : (
                    <Text theme="weak">无输出</Text>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Standard Error Card */}
            {script_output.stderr_output !== undefined && script_output.stderr_output !== null && (
              <Card style={{ marginTop: '20px' }}>
                <Card.Body
                  title={
                    <Space>
                      <span>标准错误 (stderr)</span>
                      <Tag theme="danger">
                        {script_output.stderr_output.length} 字节
                      </Tag>
                    </Space>
                  }
                >
                  {script_output.stderr_output ? (
                    <pre
                      style={{
                        background: '#fff5f5',
                        padding: '12px',
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '400px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: '#c23531',
                      }}
                    >
                      {script_output.stderr_output}
                    </pre>
                  ) : (
                    <Text theme="weak">无错误输出</Text>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Error Info Card - if task failed */}
            {script_output.error_message && (
              <Card style={{ marginTop: '20px' }}>
                <Card.Body title="错误信息">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div className="detail-section">
                      <div className="detail-label">错误码：</div>
                      <div className="detail-value">{script_output.error_code}</div>
                    </div>
                    <div className="detail-section">
                      <div className="detail-label">错误描述：</div>
                      <div className="detail-value">{script_output.error_message}</div>
                    </div>
                  </Space>
                </Card.Body>
              </Card>
            )}

            {/* AI任务总结区域 */}
            <Divider />
            {/* 只有未生成AI总结时才显示按钮 */}
            {!aiSummaryGenerated && (
              <div style={{ textAlign: 'center', margin: '20px 0' }}>
                <Button
                  theme="primary"
                  size="large"
                  loading={loadingAISummary}
                  onClick={handleAISummary}
                >
                  🤖 AI任务总结
                </Button>
              </div>
            )}

            {/* AI总结卡片 */}
            {showAISummary && (
              <Card style={{ marginTop: '20px' }}>
                <Card.Body>
                  <AICard
                    taskID={taskInfo.tid}
                    prompt={aiSummaryPrompt}
                    type="comprehensive"
                  />
                </Card.Body>
              </Card>
            )}
          </div>
        </Content.Body>
      </Content>

      {/* 任务发起弹窗 */}
      {showStartTask && (
        <StartTaskComponent
          visable={showStartTask}
          agents={agents || []}
          onClose={() => {
            setShowStartTask(false);
            setPresetTaskParams(null);
          }}
          successAction={() => {
            // 任务发起成功后的回调
            Log.debug('下钻任务发起成功');
            // 立即刷新子任务列表
            setTimeout(() => {
              fetchSubTasks();
            }, 1000);
          }}
          presetParams={presetTaskParams}
        />
      )}
    </Body>
  );
};

ScriptResult.propTypes = {
  taskInfo: PropTypes.object.isRequired,
  script_output: PropTypes.object.isRequired,
  goback: PropTypes.func,
  showGobackButton: PropTypes.bool,
  agents: PropTypes.array, // 添加agents参数
  DropAction: PropTypes.object, // Redux actions
};

export default ScriptResult;
